import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { rupiah, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BrainCircuit, AlertTriangle, Lightbulb, TrendingUp, Sparkles, Activity,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

export default function AIAnalytics() {
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sim, setSim] = useState({ fly_ash_pct: 20, waste_reduction_pct: 5, bbm_reduction_pct: 10, green_substitution_pct: 15 });
  const [simResult, setSimResult] = useState(null);
  const [prediction, setPrediction] = useState(null);

  useEffect(() => {
    api.get("/batches").then((r) => { setBatches(r.data); if (r.data[0]) setBatchId(r.data[0].id); });
    api.get("/ai/predict").then((r) => setPrediction(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const r = await api.get(`/ai/insights/${batchId}`, { params: { use_llm: true } });
      setInsight(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const runSim = async () => {
    try {
      const r = await api.post("/ai/simulate", { batch_id: batchId, ...sim });
      setSimResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">AI Analytics</h2>
        <p className="text-sm text-slate-500 mt-1">Deteksi anomali, cost driver, simulasi skenario, dan rekomendasi efisiensi berbasis AI.</p>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Pilih Batch</Label>
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger data-testid="ai-batch-select"><SelectValue placeholder="Pilih batch" /></SelectTrigger>
              <SelectContent>
                {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.no_batch} — {b.product_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generate} disabled={loading} className="bg-green-700 hover:bg-green-800" data-testid="ai-generate-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> {loading ? "Menganalisis..." : "Generate Insight"}
          </Button>
        </div>
      </Card>

      <Tabs defaultValue="insight">
        <TabsList>
          <TabsTrigger value="insight" data-testid="tab-insight">Insight & Rekomendasi</TabsTrigger>
          <TabsTrigger value="simulasi" data-testid="tab-simulasi">Simulasi Skenario</TabsTrigger>
          <TabsTrigger value="prediksi" data-testid="tab-prediksi">Prediksi HPP</TabsTrigger>
        </TabsList>

        {/* Insight */}
        <TabsContent value="insight" className="space-y-6 mt-4">
          {!insight ? (
            <Card className="p-10 border-slate-200 shadow-sm text-center text-slate-400">
              <BrainCircuit className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              Klik "Generate Insight" untuk menganalisis batch terpilih.
            </Card>
          ) : (
            <>
              <div className="grid lg:grid-cols-3 gap-6">
                <Card className="p-5 border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-red-500" /><h3 className="font-heading font-semibold">Deteksi Anomali</h3></div>
                  {insight.anomalies.length ? insight.anomalies.map((a, i) => (
                    <div key={`anom-${i}`} className="text-sm text-slate-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">{a}</div>
                  )) : <p className="text-sm text-slate-400">Tidak ada anomali.</p>}
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-blue-500" /><h3 className="font-heading font-semibold">Cost Driver</h3></div>
                  {insight.cost_drivers.map((d, i) => (
                    <div key={`drv-${i}`} className="text-sm text-slate-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-2">{d}</div>
                  ))}
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-5 w-5 text-amber-500" /><h3 className="font-heading font-semibold">Rekomendasi</h3></div>
                  {insight.recommendations.map((r, i) => (
                    <div key={`rec-${i}`} className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2">{r}</div>
                  ))}
                </Card>
              </div>
              {insight.narrative && (
                <Card className="p-6 border-slate-200 shadow-sm bg-gradient-to-br from-green-50 to-white">
                  <div className="flex items-center gap-2 mb-3"><Sparkles className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold text-slate-900">Ringkasan Eksekutif (AI)</h3></div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ai-narrative">{insight.narrative}</div>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Simulation */}
        <TabsContent value="simulasi" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-5 border-slate-200 shadow-sm space-y-4">
              <h3 className="font-heading font-semibold">Parameter Skenario</h3>
              {[
                ["fly_ash_pct", "Substitusi Fly Ash (%)"],
                ["waste_reduction_pct", "Pengurangan Waste (%)"],
                ["bbm_reduction_pct", "Pengurangan BBM (%)"],
                ["green_substitution_pct", "Substitusi Material Hijau (%)"],
              ].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-600">{l}</Label>
                  <Input type="number" value={sim[k]} onChange={(e) => setSim({ ...sim, [k]: Number(e.target.value) })} data-testid={`sim-${k}`} />
                </div>
              ))}
              <Button onClick={runSim} className="w-full bg-green-700 hover:bg-green-800" data-testid="sim-run-btn">Jalankan Simulasi</Button>
            </Card>
            <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-4">Hasil Simulasi (Sebelum vs Sesudah)</h3>
              {!simResult ? <p className="text-sm text-slate-400">Jalankan simulasi untuk melihat perbandingan.</p> : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2">Indikator</th><th className="py-2 text-right">Sebelum</th><th className="py-2 text-right">Sesudah</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["Green HPP", rupiah(simResult.before.green_hpp), rupiah(simResult.after.green_hpp)],
                          ["HPP per unit", rupiah(simResult.before.hpp_per_unit), rupiah(simResult.after.hpp_per_unit)],
                          ["Biaya Bahan", rupiah(simResult.before.bbb), rupiah(simResult.after.bbb)],
                          ["Biaya Overhead", rupiah(simResult.before.bop), rupiah(simResult.after.bop)],
                          ["Emisi CO₂ (kg)", num(simResult.before.total_emisi_kg, 1), num(simResult.after.total_emisi_kg, 1)],
                        ].map((row) => (
                          <tr key={row[0]} className="border-b border-slate-100">
                            <td className="py-2.5 text-slate-700">{row[0]}</td>
                            <td className="py-2.5 text-right tabular-nums text-slate-500">{row[1]}</td>
                            <td className="py-2.5 text-right tabular-nums font-medium text-green-700">{row[2]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-4 rounded-lg bg-green-50 text-center">
                      <p className="text-xs text-green-700">Penghematan</p>
                      <p className="font-heading font-bold text-xl text-green-700">{rupiah(simResult.saving)} <span className="text-sm">({simResult.saving_pct}%)</span></p>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-50 text-center">
                      <p className="text-xs text-blue-700">Reduksi Emisi</p>
                      <p className="font-heading font-bold text-xl text-blue-700">{num(simResult.co2_reduction, 1)} kg CO₂</p>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Prediction */}
        <TabsContent value="prediksi" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold">Prediksi HPP per Unit</h3></div>
            {prediction && prediction.series?.length ? (
              <>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-slate-50 text-center"><p className="text-xs text-slate-500">Rata-rata Historis</p><p className="font-heading font-bold text-lg">{rupiah(prediction.avg)}</p></div>
                  <div className="p-4 rounded-lg bg-green-50 text-center"><p className="text-xs text-green-700">Prediksi Batch Berikutnya</p><p className="font-heading font-bold text-lg text-green-700">{rupiah(prediction.prediction_per_unit)}</p></div>
                  <div className="p-4 rounded-lg bg-blue-50 text-center"><p className="text-xs text-blue-700">Tren</p><p className="font-heading font-bold text-lg text-blue-700">{prediction.trend >= 0 ? "+" : ""}{rupiah(prediction.trend)}</p></div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={[...prediction.series.map((v, i) => ({ name: `B${i + 1}`, hpp: v })), { name: "Prediksi", hpp: prediction.prediction_per_unit }]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}jt`} />
                    <Tooltip formatter={(v) => rupiah(v)} />
                    <Line type="monotone" dataKey="hpp" stroke="#15803d" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-slate-400 mt-3">{prediction.note}</p>
              </>
            ) : <p className="text-sm text-slate-400">Data historis belum cukup untuk prediksi.</p>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
